/**
 * Dock preferences — which apps are pinned to the dock and in what order.
 * A tiny external store (same shape as the shell store) persisted to
 * localStorage so a reviewer's curated dock survives reloads.
 *
 * `order === null` means "never customised": show every app the user may see,
 * in registry order. The moment the user reorders, removes, or adds an app we
 * materialise an explicit id list and persist it. Apps the user is not allowed
 * to see are always filtered out at render time, whatever the saved order says.
 */

const PERSIST_KEY = "radar.dock.v1";

interface DockPrefs {
  /** explicit pinned app ids in display order, or null while still default */
  order: string[] | null;
}

let prefs: DockPrefs = load();
const listeners = new Set<() => void>();

function load(): DockPrefs {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return { order: null };
    const parsed = JSON.parse(raw) as DockPrefs;
    return { order: Array.isArray(parsed.order) ? parsed.order : null };
  } catch {
    return { order: null };
  }
}

function persist() {
  try {
    localStorage.setItem(PERSIST_KEY, JSON.stringify(prefs));
  } catch {
    /* private mode / quota — a customised dock is a nicety */
  }
}

function emit(next: DockPrefs) {
  prefs = next;
  persist();
  for (const fn of listeners) fn();
}

export function subscribeDock(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getDockOrder(): string[] | null {
  return prefs.order;
}

/**
 * Resolve the ordered list of pinned app ids for a user, given the ids they are
 * allowed to see. Default (uncustomised) shows all of them in the given order.
 * A customised order keeps only ids that are still visible AND still exist.
 */
export function resolveDockIds(visibleIds: string[]): string[] {
  if (!prefs.order) return visibleIds;
  const allow = new Set(visibleIds);
  return prefs.order.filter((id) => allow.has(id));
}

/** Ids the user may see but has removed from their dock — the "add" candidates. */
export function addableDockIds(visibleIds: string[]): string[] {
  if (!prefs.order) return [];
  const shown = new Set(resolveDockIds(visibleIds));
  return visibleIds.filter((id) => !shown.has(id));
}

/** Materialise the current order from the default when first customising. */
function materialise(visibleIds: string[]): string[] {
  return prefs.order ? prefs.order.filter((id) => visibleIds.includes(id)) : [...visibleIds];
}

export function reorderDock(dragId: string, targetId: string, visibleIds: string[]): void {
  if (dragId === targetId) return;
  const list = materialise(visibleIds);
  const from = list.indexOf(dragId);
  const to = list.indexOf(targetId);
  if (from === -1 || to === -1) return;
  list.splice(from, 1);
  list.splice(list.indexOf(targetId), 0, dragId);
  emit({ order: list });
}

export function removeFromDock(id: string, visibleIds: string[]): void {
  const list = materialise(visibleIds).filter((x) => x !== id);
  emit({ order: list });
}

export function addToDock(id: string, visibleIds: string[]): void {
  const list = materialise(visibleIds);
  if (!list.includes(id)) list.push(id);
  emit({ order: list });
}

export function resetDock(): void {
  emit({ order: null });
}
